/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import React, { useState } from 'react'
import { cn } from '@/utils/css'

/** Type describing the requirements for a variant in the dropdown. */
type MtaKitDropdownVariant = { label: string; value: string }

/** A description of the available properties for this component. */
type KitVariantDropdownProps = {
  /** The variants (languages) for an MTa kit. */
  variants?: Array<MtaKitDropdownVariant>

  /** Callback handler for when the user changes the selected variant. */
  onVariantChange: (variant: string) => void
}

/** Default (English) variant, all kits are available in English. */
const DEFAULT_VARIANT: MtaKitDropdownVariant = {
  label: 'English',
  value: 'en',
}

/**
 * Dropdown selector for MTa Kits, enabling the customer to choose the language they wish to receive the kit in.
 */
const KitVariantDropdown: React.FC<KitVariantDropdownProps> = ({ variants = [], onVariantChange }) => {
  const [selectedVariant, setSelectedVariant] = useState<string>(DEFAULT_VARIANT.value)
  const combinedVariants = [DEFAULT_VARIANT, ...variants]
  const isDisabled = variants.length === 0

  const handleVariantChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newVariant = event.target.value
    setSelectedVariant(newVariant)
    onVariantChange(newVariant)
  }

  return (
    <div className={'w-full'}>
      <label htmlFor='mta-kit-variant' className='block mb-2 text-sm font-medium'>
        Select a language:
      </label>
      <div className={'relative inline-block w-full sm:w-max'}>
        <select
          id='mta-kit-variant'
          value={selectedVariant}
          onChange={handleVariantChange}
          disabled={isDisabled}
          className={cn(
            'w-full sm:w-max py-2 pl-4 pr-12 border-2 border-secondary-500 rounded-full bg-white text-primary-500 font-medium appearance-none',
            'disabled:bg-gray-100 disabled:cursor-not-allowed',
          )}
        >
          {combinedVariants.map((variant) => (
            <option key={variant.value} value={variant.value}>
              {variant.label}
            </option>
          ))}
        </select>
        <div className={'pointer-events-none absolute inset-y-0 top-1 right-6 flex items-center'}>
          <svg className={'h-4 w-4 text-primary-500'} fill={'none'} stroke={'currentColor'} viewBox={'0 0 24 24'}>
            <path strokeLinecap={'round'} strokeLinejoin={'round'} strokeWidth={'2'} d={'M19 9l-7 7-7-7'} />
          </svg>
        </div>
      </div>
    </div>
  )
}

export { KitVariantDropdown }
export type { MtaKitDropdownVariant }
